import React, { useState, useRef, useEffect, useImperativeHandle, useMemo } from "react";
import { confirmCreditCharge } from "../api/creditsApi";
import Logger from "../utils/logger";

const GEOGEBRA_DEPLOY_SCRIPT_SRC = "https://cdn.geogebra.org/apps/deployggb.js";
let geogebraScriptPromise = null;
const COMMAND_SETTLE_DELAY_MS = 10;
const RETRY_DELAY_MS = 100;
const MAX_DEFERRED_RETRIES = 3;

const ensureGeoGebraScript = () => {
  if (window.GGBApplet) {
    return Promise.resolve();
  }

  if (geogebraScriptPromise) {
    return geogebraScriptPromise;
  }

  geogebraScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector('script[data-geogebra-deploy="true"]');
    if (existingScript) {
      const handleLoad = () => resolve();
      const handleError = () => {
        geogebraScriptPromise = null;
        reject(new Error("无法加载GeoGebra脚本"));
      };

      existingScript.addEventListener("load", handleLoad, { once: true });
      existingScript.addEventListener("error", handleError, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = GEOGEBRA_DEPLOY_SCRIPT_SRC;
    script.async = true;
    script.dataset.geogebraDeploy = "true";
    script.onload = () => resolve();
    script.onerror = () => {
      geogebraScriptPromise = null;
      reject(new Error("无法加载GeoGebra脚本"));
    };
    document.body.appendChild(script);
  });

  return geogebraScriptPromise;
};

const GeoGebraViewer = React.forwardRef(({ commands, fileUrl, isVisible, onClose, isThumbnail = false, isEmbedded = false, allowDownload = true, chargeSession = null, onFinalResultDelivered = null }, ref) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionLog, setExecutionLog] = useState([]);
  const [currentCommandIndex, setCurrentCommandIndex] = useState(0);
  const [ggbLoaded, setGgbLoaded] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [debugInfo, setDebugInfo] = useState({});
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [ggbBase64, setGgbBase64] = useState(null);
  const ggbAppRef = useRef(null);
  const containerRef = useRef(null);
  const prevCommandsRef = useRef([]);
  const pendingCommandsRef = useRef([]);
  const failedCommandsRef = useRef([]);
  const failedCommandRetryCountsRef = useRef({});
  const executedCommandsRef = useRef(new Set());
  const isExecutingRef = useRef(false);
  const processingPromiseRef = useRef(null);
  const commandIndexRef = useRef(0);
  const commandRunTokenRef = useRef(0);
  const prevFileUrlRef = useRef(null);
  const initTokenRef = useRef(0);
  const initTimeoutRef = useRef(null);
  const ggbBase64Ref = useRef(null);
  const viewerRootRef = useRef(null);
  const mobileWrapperRef = useRef(null);
  const lastMobileAppliedSizeRef = useRef({ width: 0, height: 0 });
  const confirmedChargeSessionRef = useRef(null);
  const deliveredResultRef = useRef(null);
  const executionCompletedRef = useRef(false);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;
  });

  const [windowWidth, setWindowWidth] = useState(() => {
    if (typeof window === "undefined") return 360;
    return window.innerWidth;
  });

  const [windowHeight, setWindowHeight] = useState(() => {
    if (typeof window === "undefined") return 600;
    return window.innerHeight;
  });
  const [mobileViewportWidth, setMobileViewportWidth] = useState(0);
  const [mobileViewportHeight, setMobileViewportHeight] = useState(0);

  // 移动端使用真实宽高直驱 GGB，避免 transform scale 在模式切换后出现累积/污染
  const mobileAppletWidth = useMemo(() => {
    if (typeof window === "undefined") return 360;
    const viewportWidth = mobileViewportWidth > 0 ? mobileViewportWidth : (windowWidth > 0 ? windowWidth : 360);
    return Math.max(280, Math.floor(viewportWidth));
  }, [mobileViewportWidth, windowWidth]);

  const mobileAppletHeight = useMemo(() => {
    if (typeof window === "undefined") return 550;
    if (mobileViewportHeight > 0) {
      return Math.max(280, Math.floor(mobileViewportHeight));
    }
    // 估算容器高度为 windowHeight - 240 (给下方输入框留出空间)
    return Math.max(280, Math.floor(windowHeight - 240));
  }, [mobileViewportHeight, windowHeight]);
  const [containerId, setContainerId] = useState(`ggb-container-${Math.random().toString(36).substr(2, 9)}`);
  const prevIsVisibleRef = useRef(isVisible);

  // 每次组件从隐藏切换回可见时，重新生成 containerId，强制 GeoGebra 作为全新容器初始化，完美规避复用旧 ID 导致的画板缩小/残留缓存 bug
  useEffect(() => {
    if (isVisible && !prevIsVisibleRef.current) {
      const newId = `ggb-container-${Math.random().toString(36).substr(2, 9)}`;
      Logger.info(`[GeoGebraViewer] 检测到从隐藏切换回可见，更新 containerId: ${newId}`);
      setContainerId(newId);
      lastMobileAppliedSizeRef.current = { width: 0, height: 0 };
    }
    prevIsVisibleRef.current = isVisible;
  }, [isVisible]);

  useEffect(() => {
    ggbBase64Ref.current = ggbBase64;
  }, [ggbBase64]);

  const confirmFinalGgbDelivered = async () => {
    const deliveryKey = String(commands || fileUrl || "").slice(0, 200);
    if (deliveryKey && deliveredResultRef.current === deliveryKey) return;

    const sessionId = chargeSession?.sessionId;
    let confirmResult = null;
    if (sessionId && confirmedChargeSessionRef.current !== sessionId) {
      confirmedChargeSessionRef.current = sessionId;
      confirmResult = await confirmCreditCharge({
        sessionId,
        resultType: "ggb",
        resultRef: deliveryKey,
      });
      if (!confirmResult.success) {
        confirmedChargeSessionRef.current = null;
        setLoadError(confirmResult.message || "确认扣费失败，请重试");
        return;
      }
    }

    if (deliveryKey) {
      deliveredResultRef.current = deliveryKey;
    }
    if (onFinalResultDelivered) {
      onFinalResultDelivered({ commands, fileUrl }, confirmResult);
    }
  };

  // 同步原生全屏状态，避免状态与浏览器实际状态不一致
  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenElement = document.fullscreenElement;
      const root = viewerRootRef.current;
      const active =
        !!fullscreenElement &&
        !!root &&
        (fullscreenElement === root || root.contains(fullscreenElement));
      setIsFullscreen(active);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  // 检测设备类型
  useEffect(() => {
    const checkIsMobile = () => {
      const mobileCheck =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        ) || window.innerWidth <= 768;
      setIsMobile(mobileCheck);
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
      Logger.info(`设备检测: ${mobileCheck ? "移动端" : "PC端"}, 物理尺寸: ${window.innerWidth}x${window.innerHeight}`);
    };
    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);
    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  // 移动端：监听真实画板容器宽度和高度，避免仅依赖 window.innerWidth 导致切模式后缩放异常
  useEffect(() => {
    if (!isVisible || !isMobile) return;

    const updateViewportSize = () => {
      const rect = mobileWrapperRef.current?.getBoundingClientRect?.();
      const wrapperWidth = rect?.width || 0;
      const wrapperHeight = rect?.height || 0;
      const safeWidth = Math.floor(wrapperWidth);
      const safeHeight = Math.floor(wrapperHeight);

      if (safeWidth > 0) {
        setMobileViewportWidth(safeWidth);
      } else {
        setMobileViewportWidth(window.innerWidth || 360);
      }

      if (safeHeight > 0) {
        setMobileViewportHeight(safeHeight);
      } else {
        setMobileViewportHeight(0);
      }
    };

    updateViewportSize();
    const rafId = requestAnimationFrame(updateViewportSize);
    const t1 = setTimeout(updateViewportSize, 120);
    const t2 = setTimeout(updateViewportSize, 320);

    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined" && mobileWrapperRef.current) {
      resizeObserver = new ResizeObserver(() => {
        updateViewportSize();
      });
      resizeObserver.observe(mobileWrapperRef.current);
    }

    window.addEventListener("resize", updateViewportSize);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(t1);
      clearTimeout(t2);
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener("resize", updateViewportSize);
    };
  }, [isVisible, isMobile]);

  const sanitizeCommandLine = (line) => {
    if (!line) return "";
    let cleaned = line
      .replace(/\uFEFF/g, "")
      .replace(/[\u200B-\u200D\u2060]/g, "")
      .trim();

    // 清理模型常见的列表前缀，避免“1. A=(0,0)”这类行无法执行
    cleaned = cleaned
      .replace(/^\d+\s*[.)、:：]\s*/, "")
      .replace(/^[-*]\s+/, "")
      .trim();

    const commandMap = {
      "向量": "Vector",
      "线段": "Segment",
      "直线": "Line",
      "圆": "Circle",
      "点": "Point",
      "多边形": "Polygon",
      "角度": "Angle",
      "中点": "Midpoint",
      "交点": "Intersect",
      "射线": "Ray",
      "垂直线": "PerpendicularLine",
      "平行线": "Line", // 平行线在GGB中也是Line(Point, Line)
      "切线": "Tangent",
      "圆弧": "Arc",
      "扇形": "Sector",
      "椭圆": "Ellipse",
      "双曲线": "Hyperbola",
      "抛物线": "Parabola",
      "函数": "Function",
      "积分": "Integral",
      "导数": "Derivative",
      "极值": "Extremum",
      "根": "Root",
      "解": "Solutions",
      "序列": "Sequence",
      "列表": "List",
      "文本": "Text",
      "如果": "If",
      "长度": "Length",
      "距离": "Distance",
      "面积": "Area",
      "周长": "Perimeter",
      "半径": "Radius",
      "圆心": "Center",
      "焦点": "Focus",
      "顶点": "Vertex",
      "斜率": "Slope",
      "显示标签": "ShowLabel", // 注意：ShowLabel 是脚本命令，不是对象构造器
      "设置颜色": "SetColor",
      "设置线型": "SetLineStyle",
      "设置线宽": "SetLineThickness",
      "设置点大小": "SetPointSize",
      "设置标题": "SetCaption",
      "设置标签模式": "SetLabelMode",
      "设置可见性": "SetVisible",
      "设置图层": "SetLayer",
      "设置固定": "SetFixed",
      "设置轨迹": "SetTrace",
      "设置填充": "SetFilling",
      "重命名": "Rename",
      "删除": "Delete",
      "更新": "UpdateConstruction"
    };

    // 简单的正则替换：匹配命令名（通常在行首或等号后）
    // 例子: "v = 向量(A, B)" -> "v = Vector(A, B)"
    // 例子: "向量(A, B)" -> "Vector(A, B)"
    for (const [cn, en] of Object.entries(commandMap)) {
      // 匹配规则：
      // 1. 行首的命令：^命令\(
      // 2. 等号后的命令：=\s*命令\(
      // 3. 脚本命令（如设置颜色）：^命令\(
      
      // 使用正则进行替换，注意转义
      const regex = new RegExp(`(^|[=\\s])(${cn})\\s*\\(`, "g");
      if (regex.test(cleaned)) {
        cleaned = cleaned.replace(regex, `$1${en}(`);
      }
    }

    return cleaned;
  };

  const hasUnbalancedBrackets = (line) => {
    let round = 0;
    let square = 0;
    let brace = 0;
    for (const ch of line) {
      if (ch === "(") round += 1;
      if (ch === ")") round -= 1;
      if (ch === "[") square += 1;
      if (ch === "]") square -= 1;
      if (ch === "{") brace += 1;
      if (ch === "}") brace -= 1;
    }
    return round > 0 || square > 0 || brace > 0;
  };

  const isIncompleteCommand = (line) => {
    const trimmed = line.trim();
    if (!trimmed) return true;
    if (/[:=]\s*$/.test(trimmed)) return true;
    if (/[,+\-*/^]\s*$/.test(trimmed)) return true;
    if (hasUnbalancedBrackets(trimmed)) return true;
    return false;
  };

  const geogebraObjectNamePattern = "[A-Za-z][A-Za-z0-9_]*(?:_\\{[^}]+\\})?";

  const splitPotentialCommandLine = (line) => {
    const trimmed = line.trim();
    if (!trimmed) return [];

    // 修复模型偶发的换行丢失：如 "f = 1.2t = Slider(...)" 应拆成两条指令。
    return trimmed
      .replace(
        new RegExp(`([^\\s,;])\\s+(${geogebraObjectNamePattern}\\s*(?:=|:))`, "g"),
        "$1\n$2"
      )
      .replace(
        new RegExp(`(\\d(?:\\.\\d+)?)(?=(${geogebraObjectNamePattern}\\s*(?:=|:)))`, "g"),
        "$1\n"
      )
      .split(/\n+/)
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const isExecutableGeoGebraCommand = (line) => {
    const trimmed = line.trim();
    if (!trimmed) return false;
    if (new RegExp(`^${geogebraObjectNamePattern}(?:\\s*\\([^)]*\\))?\\s*(?:=|:)`).test(trimmed)) return true;
    if (/^[0-9A-Za-z+\-*/^().,\s]+=[0-9A-Za-z+\-*/^().,\s]+$/.test(trimmed)) return true;
    if (/^(?:Angle|Arc|Area|Center|CenterView|Circle|Circumcenter|Delete|Derivative|Distance|Ellipse|Extremum|Focus|Function|Hyperbola|If|Integral|Intersect|Length|Line|Midpoint|Parabola|Perimeter|Point|Polygon|Radius|Ray|Rename|Root|Sector|Segment|Sequence|SetCaption|SetColor|SetFilling|SetFixed|SetLabel|SetLabelMode|SetLayer|SetLineStyle|SetLineThickness|SetPointSize|SetTrace|SetValue|SetVisible|ShowAxes|ShowGrid|ShowLabel|Solutions|Tangent|Text|UpdateConstruction|Vector|Vertex)\s*(?:\(|\[)/i.test(trimmed)) {
      return true;
    }
    return /^[A-Z][A-Za-z0-9_]*\s*(?:\([\s\S]*\)|\[[\s\S]*\])\s*;?$/.test(trimmed);
  };

  const normalizeCommandsToArray = (text) => {
    const rawLines = text
      .split("\n")
      .map((line) => sanitizeCommandLine(line))
      .flatMap(splitPotentialCommandLine)
      .filter((line) => line.length > 0)
      .filter((line) => !line.startsWith("#") && !line.startsWith("//"))
      .filter(isExecutableGeoGebraCommand);

    const merged = [];
    let pending = "";

    for (const line of rawLines) {
      if (!pending) {
        if (isIncompleteCommand(line)) {
          pending = line;
        } else {
          merged.push(line);
        }
        continue;
      }

      pending = `${pending} ${line}`.replace(/\s+/g, " ").trim();
      if (!isIncompleteCommand(pending)) {
        merged.push(pending);
        pending = "";
      }
    }

    return merged;
  };

  const processCommandQueue = async () => {
    if (processingPromiseRef.current) {
      return processingPromiseRef.current;
    }
    if (pendingCommandsRef.current.length === 0) {
      return Promise.resolve();
    }

    const runToken = commandRunTokenRef.current;
    processingPromiseRef.current = (async () => {
      isExecutingRef.current = true;
      setIsExecuting(true);

      const failedCommands = [...failedCommandsRef.current];
      failedCommandsRef.current = [];
      while (pendingCommandsRef.current.length > 0) {
        if (runToken !== commandRunTokenRef.current) return;
        const command = pendingCommandsRef.current.shift();
        if (!command) continue;
        const commandKey = command.trim();
        if (executedCommandsRef.current.has(commandKey)) {
          Logger.debug("跳过已执行的重复GeoGebra命令", { command });
          continue;
        }
        const index = commandIndexRef.current;
        commandIndexRef.current += 1;
        setCurrentCommandIndex(commandIndexRef.current);
        const result = await executeCommand(command, index);

        setExecutionLog((prev) => [
          ...prev,
          {
            index: index + 1,
            command,
            success: result.success,
            message: result.message,
            timestamp: new Date().toLocaleTimeString(),
          },
        ]);

        if (!result.success) {
          failedCommands.push(command);
        } else {
          executedCommandsRef.current.add(commandKey);
          delete failedCommandRetryCountsRef.current[command];
        }
      }

      // 依赖对象可能在后续才创建（如样式命令先到、对象命令后到）。
      // 流式执行时保留失败命令，并在当前/后续批次的尾部重试，避免因过快执行导致作品缺件。
      let retryRound = 0;
      while (
        failedCommands.length > 0 &&
        pendingCommandsRef.current.length === 0 &&
        retryRound < MAX_DEFERRED_RETRIES
      ) {
        retryRound += 1;
        const currentRound = [...failedCommands];
        failedCommands.length = 0;
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
        if (runToken !== commandRunTokenRef.current) return;

        for (let i = 0; i < currentRound.length; i++) {
          const command = currentRound[i];
          if (runToken !== commandRunTokenRef.current) return;
          
          // 如果在重试期间收到了新指令，中断当前重试轮次，优先执行新指令
          if (pendingCommandsRef.current.length > 0) {
            failedCommands.push(...currentRound.slice(i));
            break;
          }

          const commandKey = command.trim();
          if (executedCommandsRef.current.has(commandKey)) {
            Logger.debug("跳过已成功执行的GeoGebra重试命令", { command });
            continue;
          }
          const retryCount = failedCommandRetryCountsRef.current[command] || 0;
          if (retryCount >= MAX_DEFERRED_RETRIES) {
            failedCommands.push(command);
            continue;
          }

          const index = commandIndexRef.current;
          commandIndexRef.current += 1;
          setCurrentCommandIndex(commandIndexRef.current);
          const result = await executeCommand(command, index, true);

          setExecutionLog((prev) => [
            ...prev,
            {
              index: index + 1,
              command,
              success: result.success,
              message: `重试第${retryRound}轮: ${result.message}`,
              timestamp: new Date().toLocaleTimeString(),
            },
          ]);

          if (!result.success) {
            failedCommandRetryCountsRef.current[command] = retryCount + 1;
            failedCommands.push(command);
          } else {
            executedCommandsRef.current.add(commandKey);
            delete failedCommandRetryCountsRef.current[command];
          }
        }
      }

      // 未成功的命令留到后续批次继续重试
      failedCommandsRef.current = failedCommands;
      Logger.info("GeoGebra命令队列执行完成");
    })().finally(() => {
      setIsExecuting(false);
      isExecutingRef.current = false;
      processingPromiseRef.current = null;

      // 重试等待期间可能又收到了新的流式命令；退出锁后补触发一次，避免尾部命令滞留。
      if (pendingCommandsRef.current.length > 0) {
        processCommandQueue();
      }
    });

    return processingPromiseRef.current;
  };

  const enqueueCommands = async (commandsToAdd, shouldReset = false) => {
    if (!commandsToAdd || commandsToAdd.length === 0) return;

    if (shouldReset && processingPromiseRef.current) {
      commandRunTokenRef.current += 1;
      pendingCommandsRef.current = [];
      failedCommandsRef.current = [];
      failedCommandRetryCountsRef.current = {};
      await processingPromiseRef.current;
    }

    if (shouldReset && ggbAppRef.current) {
      try {
        ggbAppRef.current.reset();
        Logger.info("执行前已清空画板");
      } catch (error) {
        Logger.error("清空画板失败", error);
      }
      pendingCommandsRef.current = [];
      failedCommandsRef.current = [];
      failedCommandRetryCountsRef.current = {};
      executedCommandsRef.current = new Set();
      commandRunTokenRef.current += 1;
      commandIndexRef.current = 0;
      setExecutionLog([]);
      setCurrentCommandIndex(0);
    }

    pendingCommandsRef.current.push(...commandsToAdd);
    await processCommandQueue();
  };

  useImperativeHandle(ref, () => ({
    clearCanvas() {
      commandRunTokenRef.current += 1;
      pendingCommandsRef.current = [];
      failedCommandsRef.current = [];
      failedCommandRetryCountsRef.current = {};
      executedCommandsRef.current = new Set();
      prevCommandsRef.current = [];
      executionCompletedRef.current = false;

      if (!ggbAppRef.current) return;

      try {
        // 只清空当前作图，不销毁或重新注入 GeoGebra applet。
        ggbAppRef.current.reset();
        ggbAppRef.current.evalCommand("ShowAxes(true)");
        ggbAppRef.current.evalCommand("ShowGrid(true)");
      } catch (error) {
        Logger.warn("清空GeoGebra画板失败", error);
      }
    },
    enqueueStreamCommands(commandText) {
      const commandsToAdd = normalizeCommandsToArray(commandText || "");
      if (commandsToAdd.length === 0) return;

      prevCommandsRef.current = [...prevCommandsRef.current, ...commandsToAdd];
      executionCompletedRef.current = false;

      if (!ggbLoaded) {
        pendingCommandsRef.current.push(...commandsToAdd);
        return;
      }

      enqueueCommands(commandsToAdd, false).then(() => {
        executionCompletedRef.current = true;
      });
    },
  }), [ggbLoaded]);

  useEffect(() => {
    if (ggbLoaded && pendingCommandsRef.current.length > 0) {
      processCommandQueue();
    }
  }, [ggbLoaded]);

  // 当指令变化且GGB加载完成后，自动执行（支持增量执行）
  useEffect(() => {
    if (!commands || !ggbLoaded) return;

    const allCommands = normalizeCommandsToArray(commands);
    const prevCommands = prevCommandsRef.current;
    let newCommands = [];
    let shouldReset = false;

    if (
      prevCommands.length > 0 &&
      allCommands.length >= prevCommands.length &&
      prevCommands.every((line, idx) => line === allCommands[idx])
    ) {
      newCommands = allCommands.slice(prevCommands.length);
    } else {
      newCommands = allCommands;
      shouldReset = true;
    }

    if (newCommands.length > 0) {
      Logger.info("检测到新的GGB指令，将增量执行", {
        newCount: newCommands.length,
        reset: shouldReset,
      });
      executionCompletedRef.current = false;
      enqueueCommands(newCommands, shouldReset).then(() => {
        executionCompletedRef.current = true;
      });
    }

    prevCommandsRef.current = allCommands;
  }, [commands, ggbLoaded]);

  // chargeSession 可能晚于命令执行完成到达；补一次确认，避免错过扣费触发。
  useEffect(() => {
    if ((!commands && !fileUrl) || !chargeSession?.sessionId) return;
    confirmFinalGgbDelivered();
  }, [chargeSession?.sessionId, commands, fileUrl]);

  // 监听 fileUrl 变化
  useEffect(() => {
    if (fileUrl && fileUrl !== prevFileUrlRef.current) {
      prevFileUrlRef.current = fileUrl;
      setGgbLoaded(false);
      setGgbBase64(null);
      setIsLoading(true);
    }
  }, [fileUrl]);

  // 外部指令清空
  useEffect(() => {
    if (!commands) {
      prevCommandsRef.current = [];
      executionCompletedRef.current = false;
      executedCommandsRef.current = new Set();
    }
    if (!fileUrl) prevFileUrlRef.current = null;
  }, [commands, fileUrl]);

  const commandArray = commands ? normalizeCommandsToArray(commands) : [];

  const cleanupApplet = () => {
    if (initTimeoutRef.current) {
      clearTimeout(initTimeoutRef.current);
      initTimeoutRef.current = null;
    }

    commandRunTokenRef.current += 1;
    executedCommandsRef.current = new Set();
    const uniqueAppId = containerId.replace(/-/g, "_");
    const currentApplet = ggbAppRef.current || window[uniqueAppId] || window.ggbApplet;

    try {
      if (currentApplet?.reset) {
        currentApplet.reset();
      }
    } catch (error) {
      Logger.warn("清理GeoGebra实例时 reset 失败", error);
    }

    if (window.ggbApplet === currentApplet) {
      window.ggbApplet = null;
    }

    if (window[uniqueAppId]) {
      try {
        delete window[uniqueAppId];
      } catch (error) {
        window[uniqueAppId] = null;
      }
    }

    if (containerRef.current) {
      containerRef.current.innerHTML = "";
    }

    ggbAppRef.current = null;
  };

  // 调试信息
  useEffect(() => {
    const updateDebugInfo = () => {
      setDebugInfo({
        ggbAppletAvailable: !!window.GGBApplet,
        ggbAppletObject: !!window.ggbApplet,
        containerRef: !!containerRef.current,
        containerDOM: !!document.getElementById(containerId),
        documentState: document.readyState,
        base64Loaded: !!ggbBase64,
      });
    };
    updateDebugInfo();
    const interval = setInterval(updateDebugInfo, 1000);
    return () => clearInterval(interval);
  }, [ggbBase64, containerId]);

  // 主初始化逻辑
  useEffect(() => {
    if (!isVisible) return;

    initTokenRef.current += 1;
    const currentInitToken = initTokenRef.current;
    Logger.info("GeoGebra组件开始初始化");
    setIsLoading(true);
    setLoadError("");
    setGgbLoaded(false);

    cleanupApplet();

    // 1. 后台下载 GGB 文件供后续缓存/下载使用，不阻塞首屏初始化
    const prepareData = async () => {
      if (fileUrl && !ggbBase64) {
        Logger.info("开始下载GGB文件:", fileUrl);
        try {
          const response = await fetch(fileUrl);
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
          const blob = await response.blob();
          const reader = new FileReader();
          reader.readAsDataURL(blob);
          reader.onloadend = () => {
            if (currentInitToken !== initTokenRef.current) {
              return;
            }
            const base64data = reader.result.split(",")[1];
            setGgbBase64(base64data);
            Logger.info(`GGB文件已下载并转换为Base64 (长度: ${base64data.length})`);
          };
          reader.onerror = (error) => {
            Logger.warn("GGB文件转Base64失败", error);
          };
        } catch (error) {
          Logger.error("下载GGB文件失败:", error);
        }
      }
    };

    // 2. 初始化 GeoGebra
    const initGeoGebra = async (base64Data, fallbackUrl) => {
      Logger.info("开始检查GeoGebra环境");
      if (!window.GGBApplet) {
        Logger.info("GGBApplet不可用，开始加载脚本");
        try {
          await ensureGeoGebraScript();
          Logger.info("GeoGebra脚本加载完成");
        } catch (error) {
          Logger.error("GeoGebra脚本加载失败", error);
          setLoadError("无法加载GeoGebra脚本");
          setIsLoading(false);
          return;
        }
      }

      if (currentInitToken !== initTokenRef.current) {
        return;
      }

      Logger.info("GGBApplet已可用，直接初始化");
      createApplet(base64Data, fallbackUrl);
    };

    const createApplet = (base64Data, fallbackUrl) => {
      if (currentInitToken !== initTokenRef.current) {
        return;
      }

      if (!containerRef.current || !document.getElementById(containerId)) {
        Logger.error("容器引用或DOM元素尚未准备好，稍后重试");
        setTimeout(() => createApplet(base64Data, fallbackUrl), 100);
        return;
      }

      Logger.info(`开始创建GeoGebra applet, Base64: ${!!base64Data}, FallbackURL: ${!!fallbackUrl}`);
      const container = containerRef.current;
      // 在thumbnail模式下，因为我们用了scale(0.25)和400%宽高，
      // 容器的实际像素尺寸会非常大（例如卡片是300x200，容器就是1200x800）
      // 我们需要确保applet充满这个大容器
      // 在thumbnail模式下，因为我们用了scale(0.25)和400%宽高，如果clientWidth没拿到正确的值，GGB会缩小到左上角
      let appletWidth = 1200;
      let appletHeight = 800;

      let appletScale = 1;

      if (isThumbnail) {
        // Thumbnail is scaled 0.25. Measure the parent card-thumbnail to calculate the exact 4x pixel dimensions
        const thumbNode = container.closest('.card-thumbnail');
        if (thumbNode && thumbNode.clientWidth > 0) {
          appletWidth = thumbNode.clientWidth * 4;
          appletHeight = thumbNode.clientHeight * 4;
        } else {
          appletWidth = 1200;
          appletHeight = 800;
        }
        appletScale = 0.25;
      } else if (isMobile) {
        // 移动端直接使用真实容器尺寸，避免 CSS 缩放与 setSize 双重补偿产生白边。
        appletScale = 1;
        appletWidth = Math.floor(mobileAppletWidth);
        appletHeight = Math.floor(mobileAppletHeight);
        Logger.info(`[移动端真实尺寸配置] 画板尺寸: ${appletWidth}x${appletHeight}`);
      } else {
        appletWidth = container.clientWidth > 0 ? container.clientWidth : 1200;
        appletHeight = isFullscreen 
            ? window.innerHeight - 60 
            : (container.clientHeight > 0 ? container.clientHeight : 500);
        appletScale = 1;
      }

      // 清理容器
      container.innerHTML = "";

      try {
        const onAppletReady = () => {
          if (currentInitToken !== initTokenRef.current) {
            return;
          }

          Logger.info("GeoGebra appletOnLoad回调触发");
          
          if (initTimeoutRef.current) {
            clearTimeout(initTimeoutRef.current);
            initTimeoutRef.current = null;
          }
          setLoadError(""); // 清除可能因为网络慢导致的超时错误提示
          
          const uniqueAppId = containerId.replace(/-/g, '_');
          const appletInstance = window[uniqueAppId] || window.ggbApplet;

          if (appletInstance) {
            Logger.info("GeoGebra对象创建成功");
            ggbAppRef.current = appletInstance;
            
            // 为了向下兼容Home.jsx中直接使用window.ggbApplet的逻辑，将主视图实例挂载到全局
            if (!isThumbnail && !isEmbedded) {
              window.ggbApplet = appletInstance;
            }

            const finishLoading = () => {
              try {
                ggbAppRef.current.setErrorDialogsActive(false);
                
                // 初始状态下如果不全屏，或者在移动端，隐藏代数区 (View ID 4)
                if (!isFullscreen || isMobile) {
                  ggbAppRef.current.setVisible("4", false);
                  Logger.info(`初始化完成：隐藏代数区 (Fullscreen: ${isFullscreen}, Mobile: ${isMobile})`);
                } else {
                  // 全屏状态下且非移动端确保代数区显示
                  ggbAppRef.current.setVisible("4", true);
                  Logger.info("初始化完成：全屏模式下显示代数区");
                }

                if (isMobile) {
                  try {
                    if (isThumbnail) {
                      ggbAppRef.current.showToolBar(false);
                      ggbAppRef.current.showMenuBar(false);
                      ggbAppRef.current.showResetIcon(false);
                    } else {
                      ggbAppRef.current.showToolBar(true);
                      ggbAppRef.current.showMenuBar(true); // 配合 0.75 缩放，重新显示菜单栏以供切换 3D 等视图
                      ggbAppRef.current.showResetIcon(true);
                    }
                    ggbAppRef.current.showAlgebraInput(false);
                    Logger.info(`移动端初始化：根据 isThumbnail=${isThumbnail} 动态配置了工具栏、菜单栏和重置按钮`);
                  } catch (toolbarErr) {
                    Logger.warn("通过 API 隐藏移动端工具栏失败", toolbarErr);
                  }
                }

                // 默认开启网格和坐标轴
                try {
                  ggbAppRef.current.evalCommand("ShowAxes(true)");
                  ggbAppRef.current.evalCommand("ShowGrid(true)");
                  Logger.info("默认开启坐标轴和网格完成");
                } catch (axesErr) {
                  Logger.warn("默认开启坐标轴/网格失败", axesErr);
                }
              } catch (e) {
                Logger.warn("禁用错误弹窗或设置初始视图失败", e);
              }
   
               setGgbLoaded(true);
               setIsLoading(false);
            };

            // 延迟加载数据
            setTimeout(() => {
              if (currentInitToken !== initTokenRef.current) {
                return;
              }

              try {
                let hasCalledFinish = false;
                // 封装一个确保只调用一次的完成函数
                const safeFinishLoading = () => {
                   if (!hasCalledFinish) {
                      hasCalledFinish = true;
                      finishLoading();
                   }
                };

                const latestBase64 = ggbBase64Ref.current || base64Data;

                if (latestBase64) {
                   Logger.info(`尝试加载Base64数据 (长度: ${latestBase64.length})`);
                   ggbAppRef.current.setBase64(latestBase64, () => {
                     Logger.info("Base64数据加载完成回调触发");
                     safeFinishLoading();
                   });
                   // 兜底：如果回调没触发，强制完成
                   setTimeout(() => {
                      if (!hasCalledFinish) {
                         Logger.warn("Base64加载回调超时，强制完成加载流程");
                         safeFinishLoading();
                      }
                   }, 1200);

                } else if (fallbackUrl) {
                   Logger.info(`尝试加载Fallback URL: ${fallbackUrl}`);
                   ggbAppRef.current.openFile(fallbackUrl);
                   // openFile 没有回调，直接完成
                   safeFinishLoading();
                } else {
                   Logger.info("无数据可加载，显示空画板");
                   safeFinishLoading();
                }
              } catch(e) {
                 Logger.error("加载数据失败", e);
                 // 如果 setBase64 失败且有 fallbackUrl，再试一次 URL
                 if (base64Data && fallbackUrl) {
                    Logger.info("Base64加载失败，尝试Fallback URL");
                    try {
                      ggbAppRef.current.openFile(fallbackUrl);
                    } catch(ex) {
                      Logger.error("Fallback URL加载也失败", ex);
                    }
                 }
                 // 出错也要完成加载，以便让用户看到画板（虽然可能是空的）并允许执行指令
                 finishLoading();
              }
            }, 100);

          } else {
            Logger.error("ggbApplet对象仍然不存在");
            setLoadError("GeoGebra对象创建失败");
            setIsLoading(false);
          }
        };

        const uniqueAppId = containerId.replace(/-/g, '_'); // Ensure valid JS identifier
        const params = {
          id: uniqueAppId,
          appName: "classic",
          width: appletWidth,
          height: appletHeight,
          showToolBar: !isThumbnail,
          showAlgebraInput: false, // 统一强制完全不显示自带的数学表达式输入框，剔除嘈杂组件
          showMenuBar: !isThumbnail,
          showResetIcon: !isThumbnail,
          allowStyleBar: !isThumbnail,
          enableRightClick: !isThumbnail,
          enableLabelDrags: !isThumbnail,
          enableShiftDragZoom: !isThumbnail,
          showAxes: true,
          showGrid: true,
          borderColor: (isThumbnail || isEmbedded) ? "transparent" : "#ccc",
          scale: appletScale, 
          appletOnLoad: onAppletReady,
          useBrowserForJS: false, // 禁用一些不必要的浏览器特性检查提升速度
          codebase: "https://cdn.geogebra.org/apps/latest/", // 强制使用CDN加速核心库加载
          // 初始挂载时决定perspective，后续全屏切换等通过动态API调整
          // 如果是缩略图或者移动端，直接使用"2"（几何/仅图形区），避免产生代数区导致的隐藏分割线和挤压
          perspective: (isThumbnail || isEmbedded || isMobile) ? "2" : "2", 
        };

        Logger.info(`创建GGBApplet实例 (空画板启动) ID: ${uniqueAppId}`);
        const applet = new window.GGBApplet(params, true);

        // 如果是主视图，才允许覆盖全局 window.ggbApplet，以兼容外部依赖
        if (!isThumbnail && !isEmbedded && window.ggbApplet) {
          window.ggbApplet = null;
        }

        // 超时保护
        initTimeoutRef.current = setTimeout(() => {
          if (!ggbAppRef.current) {
            Logger.error("GeoGebra初始化超时 (45s)");
            setLoadError("初始化超时或网络连接不佳");
            setIsLoading(false);
          }
        }, 45000); // 增加超时时间到 45s，考虑到国内网络下载 web3d 库可能比较慢

        applet.inject(containerId); // 使用动态ID
      } catch (error) {
        Logger.error("创建applet失败", error);
        setLoadError(`创建失败: ${error.message}`);
        setIsLoading(false);
      }
    };

    // 逻辑入口
    prepareData();
    initGeoGebra(ggbBase64Ref.current, fileUrl); // 优先使用内存里的Base64，否则直接用URL加载，避免首屏阻塞

    return () => {
      initTokenRef.current += 1;
      cleanupApplet();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, fileUrl, containerId, isMobile]); // 使用原生全屏API，不再因全屏切换重建Applet，避免闪烁


  // 全屏 / 窗口变化时同步画板尺寸：用 ResizeObserver 在布局稳定后测量，避免仍按旧卡片宽度 setSize 导致半屏留白
  useEffect(() => {
    if (!ggbLoaded || isThumbnail || isMobile) return;

    const applyAlgebraVisibility = () => {
      if (!ggbAppRef.current) return;
      try {
        if (isFullscreen && !isMobile) {
          ggbAppRef.current.setVisible("4", true);
          Logger.info("全屏模式：显示代数区 (View ID 4)");
        } else {
          ggbAppRef.current.setVisible("4", false);
        }
      } catch (e) {
        Logger.warn("切换代数区可见性失败", e);
      }
    };

    const applySize = () => {
      if (!ggbAppRef.current || !containerRef.current) return;
      const el = containerRef.current;
      const rect = el.getBoundingClientRect();
      let w = Math.floor(rect.width);
      let h = Math.floor(rect.height);

      // 用 document.fullscreenElement 判断，避免 fullscreenchange 早于 React 重渲染时闭包里的 isFullscreen 仍为 true，
      // 导致退出全屏后仍按全屏逻辑量宽、布局错乱。
      const root = viewerRootRef.current;
      const fsEl = document.fullscreenElement;
      const inNativeFullscreen =
        !!fsEl && !!root && (fsEl === root || root.contains(fsEl));

      // 全屏刚切换时，子节点可能尚未撑满；用全屏根节点推算可用区域作兜底
      if (inNativeFullscreen && root) {
        const rootRect = root.getBoundingClientRect();
        const headerEl = root.querySelector(".viewer-header");
        const headerH = headerEl ? headerEl.getBoundingClientRect().height : 56;
        const pad = 20;
        const availW = Math.max(320, Math.floor(rootRect.width - 2 * pad));
        const availH = Math.max(240, Math.floor(rootRect.height - headerH - 2 * pad));
        if (w < availW * 0.9) w = availW;
        if (h < availH * 0.9) h = availH;
      }

      if (w <= 0 || h <= 0) return;
      try {
        ggbAppRef.current.setSize(w, h);
      } catch (e) {
        Logger.warn("调整画板尺寸失败", e);
      }
    };

    const scheduleApplySize = () => {
      requestAnimationFrame(applySize);
    };

    /** 退出全屏后 DOM 与 GGB 内部尺寸晚一拍才稳定，多延迟几次 setSize 避免网格被撑裂 */
    const onFullscreenChange = () => {
      scheduleApplySize();
      if (!document.fullscreenElement) {
        [40, 120, 280, 500].forEach((ms) => {
          setTimeout(scheduleApplySize, ms);
        });
      }
    };

    applyAlgebraVisibility();

    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined" && containerRef.current) {
      resizeObserver = new ResizeObserver(() => {
        scheduleApplySize();
      });
      resizeObserver.observe(containerRef.current);
    }

    window.addEventListener("resize", scheduleApplySize);
    document.addEventListener("fullscreenchange", onFullscreenChange);

    const t0 = setTimeout(scheduleApplySize, 0);
    const t1 = setTimeout(scheduleApplySize, 120);
    const t2 = setTimeout(scheduleApplySize, 350);

    return () => {
      clearTimeout(t0);
      clearTimeout(t1);
      clearTimeout(t2);
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener("resize", scheduleApplySize);
      document.removeEventListener("fullscreenchange", onFullscreenChange);
    };
  }, [isFullscreen, isMobile, ggbLoaded, isThumbnail]);

  // 移动端尺寸同步：当窗口或布局高度稳定后，同步调整内部画板逻辑尺寸，避免半屏留白或键盘收起后网格挤压
  useEffect(() => {
    if (!ggbLoaded || isThumbnail || !isMobile || !ggbAppRef.current) return;
    
    const applyMobileSize = () => {
      if (!ggbAppRef.current || !mobileWrapperRef.current) return;
      const el = mobileWrapperRef.current;
      const rect = el.getBoundingClientRect();
      const width = Math.floor(rect.width);
      const height = Math.floor(rect.height);
      if (width <= 0 || height <= 0) return;

      const prev = lastMobileAppliedSizeRef.current;
      if (prev.width === width && prev.height === height) return;
      try {
        Logger.info(`[移动端动态尺寸调整] 测量并同步内部画板逻辑尺寸: ${width}x${height}`);
        ggbAppRef.current.setSize(width, height);
        lastMobileAppliedSizeRef.current = { width, height };
      } catch (e) {
        Logger.warn("移动端调整逻辑画板尺寸失败", e);
      }
    };

    // 仅做两段同步，减少重复 setSize 干扰
    const raf = requestAnimationFrame(applyMobileSize);
    const t1 = setTimeout(applyMobileSize, 220);

    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined" && mobileWrapperRef.current) {
      resizeObserver = new ResizeObserver(() => {
        applyMobileSize();
      });
      resizeObserver.observe(mobileWrapperRef.current);
    }

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t1);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [ggbLoaded, isThumbnail, isMobile]);

  // 进入全屏后轻量居中视角（不改动坐标比例），避免图形贴在视口一侧
  useEffect(() => {
    if (!ggbLoaded || !isFullscreen) return;
    const t = setTimeout(() => {
      try {
        if (ggbAppRef.current) {
          ggbAppRef.current.evalCommand("CenterView((0,0))");
        }
      } catch (e) {
        Logger.warn("全屏后 CenterView 失败", e);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [isFullscreen, ggbLoaded]);

  // 执行单个命令
  const executeCommand = async (command, index, isRetry = false) => {
    if (!ggbAppRef.current || !command.trim()) {
      return { success: false, message: "GeoGebra未就绪或命令为空" };
    }

    try {
      const preparedCommand = sanitizeCommandLine(command);
      if (!preparedCommand) {
        return { success: false, message: "命令为空或被清理后为空" };
      }
      await waitForCommandDependencies(preparedCommand, isRetry);
      
      if (!isRetry) {
        Logger.info(`执行GeoGebra命令 [${index + 1}]: ${preparedCommand}`);
      } else {
        Logger.debug(`重试GeoGebra命令 [${index + 1}]: ${preparedCommand}`);
      }
      
      const executeResult = ggbAppRef.current.evalCommand(preparedCommand);
      if (executeResult === false) {
        return { success: false, message: `命令执行返回false: ${preparedCommand}` };
      }
      await waitForCommandToSettle(preparedCommand);
      return { success: true, message: `命令执行成功: ${preparedCommand}` };
    } catch (error) {
      if (!isRetry) {
        Logger.error(`GeoGebra命令执行失败 [${index + 1}]: ${command}`, error);
      }
      return { success: false, message: `命令执行失败: ${error.message}` };
    }
  };

  const getCommandDependencyNames = (command) => {
    const objectNameCapture = "([A-Za-z][A-Za-z0-9_]*(?:_\\{[^}]+\\})?)";
    const dependencyCommandMatch = command.match(
      new RegExp(`^(SetColor|SetLabel|ShowLabel|SetCaption|SetLineStyle|SetLineThickness|SetPointSize|SetLabelMode|SetValue|SetVisible|SetLayer|SetFixed|SetTrace|SetFilling|Rename|Delete)\\s*\\(\\s*${objectNameCapture}`, "i")
    );
    return dependencyCommandMatch?.[2] ? [dependencyCommandMatch[2]] : [];
  };

  const waitForCommandDependencies = async (command, isRetry = false) => {
    const dependencyNames = getCommandDependencyNames(command);
    if (dependencyNames.length === 0) return;

    const maxWait = isRetry ? 100 : 2200;
    const start = Date.now();
    while (Date.now() - start < maxWait) {
      const allReady = dependencyNames.every((name) => {
        try {
          return !!ggbAppRef.current?.getObjectType(name);
        } catch (_) {
          return false;
        }
      });

      if (allReady) return;
      await new Promise((resolve) => setTimeout(resolve, 20));
    }
  };

  const getAssignedObjectName = (command) => {
    const match = command.match(/^\s*([A-Za-z][A-Za-z0-9_]*(?:_\{[^}]+\})?)\s*(?:=|:)/);
    return match?.[1] || null;
  };

  const waitForCommandToSettle = async (command) => {
    const assignedName = getAssignedObjectName(command);
    const start = Date.now();

    while (assignedName && Date.now() - start < 1200) {
      try {
        if (ggbAppRef.current?.getObjectType(assignedName)) {
          break;
        }
      } catch (_) {
        // 对象尚未注册完成，继续短暂等待
      }
      await new Promise((resolve) => setTimeout(resolve, 20));
    }

    if (COMMAND_SETTLE_DELAY_MS > 0) {
      await new Promise((resolve) => setTimeout(resolve, COMMAND_SETTLE_DELAY_MS));
    }
  };

  // 执行所有命令（用于重置/全量执行）
  const executeAllCommands = async (shouldReset = true) => {
    if (!ggbAppRef.current || commandArray.length === 0) {
      Logger.warn("GeoGebra未就绪或没有命令");
      return;
    }
    await enqueueCommands(commandArray, shouldReset);
  };

  // 重置GeoGebra画板
  const resetCanvas = () => {
    Logger.info("重置画板：清空并重新执行命令");
    executeAllCommands(true);
  };

  // 下载GeoGebra文件
  const downloadGGBFile = () => {
    if (!ggbAppRef.current) {
      Logger.warn("ggbApplet 尚未就绪，无法下载文件");
      return;
    }

    const triggerDownload = (base64) => {
      try {
        const binaryString = atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        const blob = new Blob([bytes], {
          type: "application/vnd.geogebra.file",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `AI4GGB_作品_${new Date()
          .toISOString()
          .slice(0, 19)
          .replace(/:/g, "-")}.ggb`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        Logger.info("GeoGebra文件下载已启动");
      } catch (e) {
        Logger.error("生成 GeoGebra 文件失败", e);
      }
    };

    try {
      const maybeString = ggbAppRef.current.getBase64(triggerDownload);
      if (typeof maybeString === "string" && maybeString.length) {
        triggerDownload(maybeString);
      }
    } catch (error) {
      Logger.error("下载GeoGebra文件失败", error);
    }
  };

  if (!isVisible) {
    return null;
  }

  const toggleFullscreen = async () => {
    const root = viewerRootRef.current;
    if (!root) return;

    try {
      const fullscreenElement = document.fullscreenElement;
      const isCurrentViewerFullscreen =
        !!fullscreenElement &&
        (fullscreenElement === root || root.contains(fullscreenElement));

      if (isCurrentViewerFullscreen) {
        await document.exitFullscreen();
      } else {
        await root.requestFullscreen();
      }
    } catch (error) {
      Logger.error("切换全屏失败", error);
    }
  };

  const ViewerContent = (
    <div ref={viewerRootRef} className={`geogebra-viewer ${isFullscreen ? "fullscreen" : ""} ${isThumbnail ? "thumbnail-mode" : ""} ${isEmbedded ? "embedded-mode" : ""}`} style={isThumbnail ? {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      zIndex: 1,
      background: 'transparent',
      border: 'none',
      boxShadow: 'none',
      borderRadius: 0,
      padding: 0,
      display: 'block'
    } : isEmbedded ? {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '10px',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column'
    } : {
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div className="viewer-header" style={isThumbnail ? { display: 'none' } : {}}>
        <h3>
          <i className="ri-draft-line"></i>
        </h3>
        <div className="viewer-controls">
          {ggbLoaded && (
            <>
              {allowDownload && (
                <button
                  className="control-btn"
                  onClick={downloadGGBFile}
                  disabled={isExecuting}
                  title="下载GGB文件"
                >
                  <i className="ri-download-line"></i>
                  <span>下载</span>
                </button>
              )}
              <button
                className="control-btn"
                onClick={resetCanvas}
                disabled={isExecuting}
                title="重置画板"
              >
                <i className="ri-refresh-line"></i>
                <span>重置</span>
              </button>
              {!isMobile && (
                <button
                  className="control-btn"
                  onClick={toggleFullscreen}
                  title={isFullscreen ? "退出全屏" : "全屏"}
                >
                  <i
                    className={
                      isFullscreen
                        ? "ri-fullscreen-exit-line"
                        : "ri-fullscreen-line"
                    }
                  ></i>
                  <span>{isFullscreen ? "退出" : "全屏"}</span>
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="viewer-content" style={isThumbnail ? { 
        height: '100%', 
        width: '100%', 
        padding: 0,
        pointerEvents: 'none',
        position: 'relative'
      } : {
        flex: 1,
        width: '100%',
        height: '100%',
        padding: 0
      }}>
        <div
          className="geogebra-wrapper"
          ref={mobileWrapperRef}
          style={{ 
            position: "relative", 
            width: "100%", 
            height: "100%",
            display: isMobile ? "flex" : "block",
            justifyContent: isMobile ? "center" : "initial",
            alignItems: isMobile ? "center" : "initial",
            backgroundColor: isThumbnail ? "transparent" : "#ffffff", // 避免由于宽高比例微小偏差导致的黑边问题
          }}
        >
          <div
            id={containerId} // 使用动态ID
            ref={containerRef}
            style={isMobile ? {
              width: "100%",
              height: "100%",
              position: "absolute",
              top: 0,
              left: 0,
              flexShrink: 0,
              border: "none",
              backgroundColor: isThumbnail ? "transparent" : "#ffffff", // 避免由于宽高比例微小偏差导致的黑边问题
            } : {
              width: "100%",
              height: "100%",
              flexShrink: 0,
              border: (isThumbnail || isEmbedded) ? "none" : "1px solid #ccc",
              backgroundColor: isThumbnail ? "transparent" : "#ffffff", // 避免由于宽高比例微小偏差导致的黑边问题
            }}
          />
          {isLoading && (
            <div
              className="loading-state"
              style={{
                position: "absolute",
                zIndex: 10,
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#fff",
              }}
            >
              <div className="spinner"></div>
              <p>正在加载GeoGebra画板...</p>
              <div
                style={{ fontSize: "0.8rem", color: "#666", marginTop: "10px" }}
              >
                <p>调试信息：</p>
                <p>
                  • GGBApplet可用: {debugInfo.ggbAppletAvailable ? "是" : "否"}
                </p>
                <p>• 文档状态: {debugInfo.documentState}</p>
                <p>
                  • ggbApplet对象:{" "}
                  {debugInfo.ggbAppletObject ? "存在" : "不存在"}
                </p>
                <p>• 数据加载: {debugInfo.base64Loaded ? "完成" : "等待中"}</p>
              </div>
            </div>
          )}
          {loadError && (
            <div
              className="error-state"
              style={{
                position: "absolute",
                zIndex: 10,
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#fff",
                padding: "20px",
              }}
            >
              <i className="ri-error-warning-line"></i>
              <h4>GeoGebra画板加载失败</h4>
              <p>{loadError}</p>
              <div
                style={{ fontSize: "0.8rem", color: "#666", marginTop: "10px" }}
              >
                <p>调试信息：</p>
                <p>
                  • GGBApplet可用: {debugInfo.ggbAppletAvailable ? "是" : "否"}
                </p>
                <p>
                  • ggbApplet对象:{" "}
                  {debugInfo.ggbAppletObject ? "存在" : "不存在"}
                </p>
                <p>• 容器引用: {!!containerRef.current ? "存在" : "不存在"}</p>
              </div>
              <button
                className="control-btn"
                onClick={() => {
                  setLoadError("");
                  setIsLoading(true);
                  setGgbLoaded(false);
                  setGgbBase64(null); // 清除Base64以便重试下载
                  // 重新加载GeoGebra
                  window.location.reload();
                }}
              >
                <i className="ri-refresh-line"></i>
                刷新页面重试
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return ViewerContent;
});

export default GeoGebraViewer;
